import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, Input } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIcon } from '@angular/material/icon';
import { Router } from '@angular/router';
import { UploadUserAgreementDto, UserAgreementType } from '@flexyoo/api/models';
import { EmployeeApiService, TenantGroupGeneralPolicyApiService } from '@flexyoo/api/services';
import { AuthenticationService } from '@flexyoo/auth';
import { environment } from '@flexyoo/environment';
import { downloadURI } from '@flexyoo/shared/functions';
import { FileValidationService, LangService } from '@flexyoo/shared/services';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-user-agreement-sign-upload-block',
  templateUrl: './user-agreement-sign-upload-block.component.html',
  styleUrl: './user-agreement-sign-upload-block.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIcon, MatButtonModule, TranslateModule, AsyncPipe, FlexLayoutModule, MatCardModule],
})
export class UserAgreementSignUploadBlockComponent {
  public readonly userAgreementType = input.required<UserAgreementType>();
  @Input() public isQuillSignPossible: boolean;

  private readonly auth = inject(AuthenticationService);
  private readonly router = inject(Router);
  private readonly langService = inject(LangService);
  private readonly fileValidationService = inject(FileValidationService);
  private readonly employeeApi = inject(EmployeeApiService);
  private readonly tenantGroupGeneralPolicyApi = inject(TenantGroupGeneralPolicyApiService);

  protected readonly incorrectFileTypeError$ = new BehaviorSubject<boolean>(false);

  get staticUserAgreementHowTo(): string {
    const user = this.auth.getCurrentUser();
    if (user && user.Language) {
      return `${
        environment.static
      }/instructions/How to sign Agreement ${user.Language.toUpperCase()}.pdf`;
    }

    return '#';
  }

  protected onUserAgreementFileDownload() {
    const userAgreementType = this.userAgreementType();
    if (userAgreementType === null || userAgreementType === undefined) {
      return;
    }

    this.employeeApi
      .userAgreementTemplateDownload(this.auth.getCurrentUser().EmployeeId, { userAgreementType })
      .pipe(map((r) => r.body))
      .subscribe((result: Blob) => {
        if (result) {
          const url = window.URL.createObjectURL(result);
          downloadURI(url, `Flexyoo user agreement.pdf`, false);
        }
      });
  }

  protected onUserAgreementFileChange(files) {
    this.incorrectFileTypeError$.next(false);

    if (files.length === 0) {
      return;
    }

    const fileToUpload = files[0] as File;

    if (fileToUpload.type !== 'application/pdf') {
      this.incorrectFileTypeError$.next(true);
      return;
    }

    if (!this.fileValidationService.isSizeValid(fileToUpload)) {
      return;
    }

    const userAgreementType = this.userAgreementType();
    if (userAgreementType === null || userAgreementType === undefined) {
      return;
    }

    const formData = new FormData();
    formData.append('File', fileToUpload, fileToUpload.name);
    formData.append('UserAgreementType', userAgreementType.toString());
    this.employeeApi.userAgreementUpload(formData).subscribe((result: UploadUserAgreementDto) => {
      if (result) {
        this.router.navigate(['/login']);
      }
    });
  }

  protected onGeneralPolicyDownload(): void {
    let lang = this.langService.lang;
    if (lang === 'en') {
      lang = this.auth.getCurrentUser().Language;
    }

    const tenantGroupId = this.auth.getCurrentUser()?.TenantGroupId;
    if (!tenantGroupId) {
      return;
    }

    this.tenantGroupGeneralPolicyApi.getFile(tenantGroupId, lang).subscribe((result) => {
      if (result?.body) {
        const url = window.URL.createObjectURL(result.body);
        downloadURI(url, result.headers.get('x-file-name') ?? 'Flexyoo general policy.pdf', false);
      }
    });
  }
}
